import { isNil, isString } from '@pct-ts/typeguard';
import { MIME_EXTENSION_MAP, type MimeTypes } from '@pct-ng/common/file';

/**
 * Expands a single MIME type to include its corresponding file extensions.
 * This improves file picker compatibility on Windows where MIME type filtering
 * may not work correctly.
 *
 * @example
 * expandMimeType('image/jpeg') // => ['image/jpeg', '.jpg', '.jpeg', '.jpe', '.jfif']
 */
function expandMimeType(mimeType: string): string[] {
  const extensions = MIME_EXTENSION_MAP[mimeType as MimeTypes];
  if (extensions) {
    return [mimeType, ...extensions];
  }
  return [mimeType];
}

/**
 * Joins MIME types into a comma-separated string for the HTML input accept attribute.
 * Automatically expands known MIME types to include their file extensions for better
 * cross-platform compatibility (especially on Windows).
 *
 * @example
 * joinMimeTypes(['image/jpeg']) // => 'image/jpeg,.jpg,.jpeg,.jpe,.jfif'
 * joinMimeTypes(['image/jpeg', 'image/png']) // => 'image/jpeg,.jpg,.jpeg,.jpe,.jfif,image/png,.png'
 * joinMimeTypes('custom/type') // => 'custom/type' (unknown types pass through)
 */
export function joinMimeTypes(
  mimetypes: string | MimeTypes | MimeTypes[] | null | undefined,
): string {
  if (isNil(mimetypes)) {
    return '';
  }

  if (isString(mimetypes)) {
    // Check if it's a known MIME type that should be expanded
    if (mimetypes in MIME_EXTENSION_MAP) {
      return expandMimeType(mimetypes).join(',');
    }
    // Unknown or already formatted string - return as-is
    return mimetypes;
  }

  if (Array.isArray(mimetypes)) {
    const expanded = mimetypes.flatMap(mimeType => expandMimeType(mimeType));
    return Array.from(new Set(expanded)).join(',');
  }

  return mimetypes;
}
